/**
 * =============================================================================
 * ASSET WORKSPACE - Unified Asset Management Console
 * =============================================================================
 * CONSOLIDATES: Parks, Plots, Assets, PPM Schedule, Compliance
 * 
 * Single workspace replacing 5+ separate pages.
 * Tab-driven navigation, inline actions, contextual panels.
 * 
 * Route: /phs/assets?tab=parks|plots|register|ppm|compliance
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, Home, Database, Calendar, Shield, Search, Plus, 
  RefreshCw, Filter, Eye, Edit, Trash2, Briefcase, MapPin,
  AlertTriangle, CheckCircle, Clock, Flame, Zap, MoreHorizontal,
  ChevronRight, X, FileText, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAssetUrlState } from '@/hooks/useUrlState';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { PHSCommandLayout, TOKENS } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// WORKSPACE TABS CONFIGURATION
// =============================================================================
const WORKSPACE_TABS = [
  { key: 'parks', label: 'Parks', icon: Building2, description: 'Site locations' },
  { key: 'plots', label: 'Plots', icon: Home, description: 'Individual units' },
  { key: 'register', label: 'Asset Register', icon: Database, description: 'All assets' },
  { key: 'ppm', label: 'PPM Schedule', icon: Calendar, description: 'Planned maintenance' },
  { key: 'compliance', label: 'Compliance', icon: Shield, description: 'Certifications & expiries' },
];

// =============================================================================
// WORKSPACE KPI STRIP
// =============================================================================
function WorkspaceKPIStrip({ metrics, activeTab }) {
  const tabKPIs = {
    parks: [
      { label: 'Total Parks', value: metrics.parks_count || 0, color: '#0D9488' },
      { label: 'Total Plots', value: metrics.plots_count || 0, color: '#6366F1' },
      { label: 'Active Jobs', value: metrics.active_jobs || 0, color: '#F59E0B' },
      { label: 'Compliance Issues', value: metrics.compliance_issues || 0, color: '#DC2626' },
    ],
    plots: [
      { label: 'Total Plots', value: metrics.plots_count || 0, color: '#0D9488' },
      { label: 'LPG Heating', value: metrics.lpg_count || 0, color: '#F97316' },
      { label: 'Electric', value: metrics.electric_count || 0, color: '#EAB308' },
      { label: 'Compliance Due', value: metrics.compliance_due || 0, color: '#DC2626' },
    ],
    register: [
      { label: 'Total Assets', value: metrics.assets_count || 0, color: '#0D9488' },
      { label: 'Boilers', value: metrics.boilers || 0, color: '#F97316' },
      { label: 'Electrical', value: metrics.electrical || 0, color: '#EAB308' },
      { label: 'Due Service', value: metrics.due_service || 0, color: '#DC2626' },
    ],
    ppm: [
      { label: 'Scheduled', value: metrics.ppm_scheduled || 0, color: '#0D9488' },
      { label: 'Overdue', value: metrics.ppm_overdue || 0, color: '#DC2626' },
      { label: 'This Month', value: metrics.ppm_this_month || 0, color: '#F59E0B' },
      { label: 'Completed', value: metrics.ppm_completed || 0, color: '#22C55E' },
    ],
    compliance: [
      { label: 'Compliant', value: metrics.compliant || 0, color: '#22C55E' },
      { label: 'Expiring 7d', value: metrics.expiring_7 || 0, color: '#F59E0B' },
      { label: 'Expiring 30d', value: metrics.expiring_30 || 0, color: '#EAB308' },
      { label: 'Expired', value: metrics.expired || 0, color: '#DC2626' },
    ],
  };

  const kpis = tabKPIs[activeTab] || tabKPIs.parks;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {kpis.map((kpi, idx) => (
        <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs text-slate-500">{kpi.label}</p>
          <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// PARKS TAB CONTENT
// =============================================================================
function ParksTabContent({ parks, loading, onRefresh, onAction, searchTerm, setSearchTerm }) {
  const filteredParks = useMemo(() => {
    if (!searchTerm) return parks;
    const search = searchTerm.toLowerCase();
    return parks.filter(p =>
      p.name?.toLowerCase().includes(search) ||
      p.postcode?.toLowerCase().includes(search)
    );
  }, [parks, searchTerm]);

  return (
    <div data-testid="parks-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search parks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button size="sm" onClick={() => onAction('add-park')}>
          <Plus className="w-4 h-4 mr-1" /> Add Park
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Park</th>
              <th className="text-left p-3 font-medium text-slate-600">Location</th>
              <th className="text-left p-3 font-medium text-slate-600">Plots</th>
              <th className="text-left p-3 font-medium text-slate-600">Jobs</th>
              <th className="text-left p-3 font-medium text-slate-600">Compliance</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredParks.map(park => (
              <tr key={park.id || park.park_id} className="hover:bg-slate-50">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-teal-600" />
                    <span className="font-medium text-slate-800">{park.name}</span>
                  </div>
                </td>
                <td className="p-3 text-slate-600 text-xs">{park.postcode || park.address}</td>
                <td className="p-3 font-medium">{park.total_plots || park.plot_count || 0}</td>
                <td className="p-3">
                  <Badge variant={park.active_jobs > 0 ? 'default' : 'secondary'}>
                    {park.active_jobs || 0}
                  </Badge>
                </td>
                <td className="p-3">
                  {park.compliance_issues > 0 ? (
                    <Badge variant="destructive" className="text-xs">
                      {park.compliance_issues} issues
                    </Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700 text-xs">OK</Badge>
                  )}
                </td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAction('view-park', park)}>
                        <Eye className="w-4 h-4 mr-2" /> View Plots
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAction('new-job', park)}>
                        <Briefcase className="w-4 h-4 mr-2" /> Raise Job
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onAction('edit-park', park)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {filteredParks.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No parks found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// PLOTS TAB CONTENT
// =============================================================================
function PlotsTabContent({ plots, parks, loading, onRefresh, onAction, filters, setFilters }) {
  const filteredPlots = useMemo(() => {
    let result = plots;
    if (filters.park && filters.park !== 'all') {
      result = result.filter(p => p.park_id === filters.park || p.park_name === filters.park);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(p =>
        p.pitch_or_unit?.toLowerCase().includes(search) ||
        p.park_name?.toLowerCase().includes(search)
      );
    }
    return result;
  }, [plots, filters]);

  return (
    <div data-testid="plots-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search plots..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        <Select value={filters.park || 'all'} onValueChange={(v) => setFilters({ ...filters, park: v })}>
          <SelectTrigger className="w-44 h-9">
            <SelectValue placeholder="All Parks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Parks</SelectItem>
            {parks.map(p => (
              <SelectItem key={p.id || p.park_id} value={p.id || p.park_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Plot/Unit</th>
              <th className="text-left p-3 font-medium text-slate-600">Park</th>
              <th className="text-left p-3 font-medium text-slate-600">Type</th>
              <th className="text-left p-3 font-medium text-slate-600">Heating</th>
              <th className="text-left p-3 font-medium text-slate-600">Next Service</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredPlots.map(plot => (
              <tr key={plot.asset_id || plot.id} className="hover:bg-slate-50">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-800">{plot.pitch_or_unit || plot.address}</span>
                  </div>
                </td>
                <td className="p-3 text-slate-600 text-xs">{plot.park_name}</td>
                <td className="p-3"><Badge variant="outline" className="text-xs">{plot.asset_type || '-'}</Badge></td>
                <td className="p-3">
                  <div className="flex items-center gap-1 text-xs">
                    {plot.heating_type === 'LPG' && <Flame className="w-3 h-3 text-orange-500" />}
                    {plot.heating_type === 'Electric' && <Zap className="w-3 h-3 text-yellow-500" />}
                    <span>{plot.heating_type || '-'}</span>
                  </div>
                </td>
                <td className="p-3 text-xs text-slate-500">
                  {plot.next_inspection_due ? new Date(plot.next_inspection_due).toLocaleDateString() : '-'}
                </td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAction('view-plot', plot)}>
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAction('new-job', plot)}>
                        <Briefcase className="w-4 h-4 mr-2" /> Raise Job
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {filteredPlots.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No plots found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// COMPLIANCE TAB CONTENT
// =============================================================================
function ComplianceTabContent({ assets, loading, onRefresh, onAction, filters, setFilters }) {
  const now = new Date();
  
  const enrichedAssets = useMemo(() => {
    return assets.map(asset => {
      const expiry = asset.expiry_date ? new Date(asset.expiry_date) : null;
      const daysUntilExpiry = expiry ? Math.ceil((expiry - now) / 86400000) : 999;
      return { ...asset, days_until_expiry: daysUntilExpiry };
    });
  }, [assets]);

  const filteredAssets = useMemo(() => {
    let result = enrichedAssets;
    if (filters.status === 'expiring_7') {
      result = result.filter(a => a.days_until_expiry <= 7 && a.days_until_expiry > 0);
    } else if (filters.status === 'expiring_30') {
      result = result.filter(a => a.days_until_expiry <= 30 && a.days_until_expiry > 0);
    } else if (filters.status === 'expired') {
      result = result.filter(a => a.days_until_expiry <= 0);
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(a => a.asset_name?.toLowerCase().includes(search) || a.park_name?.toLowerCase().includes(search));
    }
    return result;
  }, [enrichedAssets, filters]);

  return (
    <div data-testid="compliance-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search assets..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'expiring_7', 'expiring_30', 'expired'].map(status => (
            <Button
              key={status}
              variant={filters.status === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters({ ...filters, status })}
              className={filters.status === status ? 'bg-teal-600' : ''}
            >
              {status === 'all' ? 'All' : status === 'expiring_7' ? '7 Days' : status === 'expiring_30' ? '30 Days' : 'Expired'}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Asset</th>
              <th className="text-left p-3 font-medium text-slate-600">Type</th>
              <th className="text-left p-3 font-medium text-slate-600">Certificate</th>
              <th className="text-left p-3 font-medium text-slate-600">Expiry</th>
              <th className="text-left p-3 font-medium text-slate-600">Status</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredAssets.map(asset => (
              <tr key={asset.id} className="hover:bg-slate-50">
                <td className="p-3">
                  <div>
                    <p className="font-medium text-slate-800">{asset.asset_name}</p>
                    <p className="text-xs text-slate-500">{asset.park_name}</p>
                  </div>
                </td>
                <td className="p-3"><Badge variant="outline" className="text-xs">{asset.compliance_type_label || asset.compliance_type}</Badge></td>
                <td className="p-3 text-xs text-slate-600 font-mono">{asset.certificate_ref || '-'}</td>
                <td className="p-3 text-xs text-slate-500">
                  {asset.expiry_date ? new Date(asset.expiry_date).toLocaleDateString() : '-'}
                </td>
                <td className="p-3">
                  <Badge className={
                    asset.days_until_expiry <= 0 ? 'bg-red-100 text-red-700' :
                    asset.days_until_expiry <= 7 ? 'bg-amber-100 text-amber-700' :
                    asset.days_until_expiry <= 30 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }>
                    {asset.days_until_expiry <= 0 ? `${Math.abs(asset.days_until_expiry)}d overdue` :
                     `${asset.days_until_expiry}d`}
                  </Badge>
                </td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAction('schedule-inspection', asset)}>
                        <Calendar className="w-4 h-4 mr-2" /> Schedule Inspection
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAction('new-job', asset)}>
                        <Briefcase className="w-4 h-4 mr-2" /> Raise Job
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {filteredAssets.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No compliance records found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// PPM TAB CONTENT
// =============================================================================
function PPMTabContent({ ppmItems, loading, onRefresh, onAction, filters, setFilters }) {
  const filteredItems = useMemo(() => {
    let result = ppmItems;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(p => p.asset_name?.toLowerCase().includes(search) || p.task_name?.toLowerCase().includes(search));
    }
    return result;
  }, [ppmItems, filters]);

  return (
    <div data-testid="ppm-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search PPM tasks..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button size="sm" onClick={() => onAction('add-ppm')}>
          <Plus className="w-4 h-4 mr-1" /> Add PPM Task
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Task</th>
              <th className="text-left p-3 font-medium text-slate-600">Asset</th>
              <th className="text-left p-3 font-medium text-slate-600">Frequency</th>
              <th className="text-left p-3 font-medium text-slate-600">Next Due</th>
              <th className="text-left p-3 font-medium text-slate-600">Status</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredItems.map(item => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800">{item.task_name || item.description}</td>
                <td className="p-3 text-slate-600 text-xs">{item.asset_name || '-'}</td>
                <td className="p-3"><Badge variant="outline" className="text-xs">{item.frequency || 'Annual'}</Badge></td>
                <td className="p-3 text-xs text-slate-500">
                  {item.next_due ? new Date(item.next_due).toLocaleDateString() : '-'}
                </td>
                <td className="p-3">
                  <Badge className={item.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                    {item.status || 'Scheduled'}
                  </Badge>
                </td>
                <td className="p-3">
                  <Button variant="ghost" size="sm" onClick={() => onAction('create-ppm-job', item)}>
                    <Briefcase className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filteredItems.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No PPM tasks found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// ASSET REGISTER TAB CONTENT
// =============================================================================
function AssetRegisterTabContent({ assets, loading, onRefresh, onAction, filters, setFilters }) {
  const filteredAssets = useMemo(() => {
    let result = assets;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(a => a.asset_name?.toLowerCase().includes(search) || a.park_name?.toLowerCase().includes(search));
    }
    return result;
  }, [assets, filters]);

  return (
    <div data-testid="register-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search assets..."
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button size="sm" onClick={() => onAction('add-asset')}>
          <Plus className="w-4 h-4 mr-1" /> Add Asset
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-slate-600">Asset</th>
              <th className="text-left p-3 font-medium text-slate-600">Location</th>
              <th className="text-left p-3 font-medium text-slate-600">Type</th>
              <th className="text-left p-3 font-medium text-slate-600">Serial/Model</th>
              <th className="text-left p-3 font-medium text-slate-600">Last Service</th>
              <th className="text-left p-3 font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredAssets.map(asset => (
              <tr key={asset.asset_id || asset.id} className="hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800">{asset.asset_name || asset.description}</td>
                <td className="p-3 text-xs text-slate-600">{asset.park_name} - {asset.pitch_or_unit}</td>
                <td className="p-3"><Badge variant="outline" className="text-xs">{asset.asset_type}</Badge></td>
                <td className="p-3 text-xs text-slate-500 font-mono">{asset.serial_number || asset.model || '-'}</td>
                <td className="p-3 text-xs text-slate-500">
                  {asset.last_service ? new Date(asset.last_service).toLocaleDateString() : '-'}
                </td>
                <td className="p-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAction('view-asset', asset)}>
                        <Eye className="w-4 h-4 mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAction('new-job', asset)}>
                        <Briefcase className="w-4 h-4 mr-2" /> Raise Job
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAction('edit-asset', asset)}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {filteredAssets.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">
                {loading ? 'Loading...' : 'No assets found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN ASSET WORKSPACE
// =============================================================================
function AssetWorkspace({ wyldecrestUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // URL-driven state
  const { getParam, updateParams } = useAssetUrlState();
  const activeTab = getParam('tab', 'parks');
  const urlSearch = getParam('search', '');
  const urlPark = getParam('park', '');
  const urlStatus = getParam('status', '');
  
  const [parks, setParks] = useState([]);
  const [plots, setPlots] = useState([]);
  const [assets, setAssets] = useState([]);
  const [complianceAssets, setComplianceAssets] = useState([]);
  const [ppmItems, setPpmItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({});

  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [parksRes, plotsRes, complianceRes] = await Promise.all([
        axios.get(`${API}/api/wyldecrest/parks`, { headers }),
        axios.get(`${API}/api/ppm-core/assets`, { params: { limit: 500 }, headers }),
        axios.get(`${API}/api/phs/compliance/assets`, { headers }).catch(() => ({ data: { assets: [] } })),
      ]);
      
      const allParks = parksRes.data.parks || [];
      const allPlots = plotsRes.data.assets || [];
      const allCompliance = complianceRes.data.assets || [];
      
      setParks(allParks);
      setPlots(allPlots);
      setAssets(allPlots);
      setComplianceAssets(allCompliance);
      setPpmItems([]); // PPM items from separate endpoint if exists
      
      // Calculate metrics
      const now = new Date();
      setMetrics({
        parks_count: allParks.length,
        plots_count: allPlots.length,
        assets_count: allPlots.length,
        active_jobs: allParks.reduce((sum, p) => sum + (p.active_jobs || 0), 0),
        compliance_issues: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          return exp && exp < now;
        }).length,
        lpg_count: allPlots.filter(p => p.heating_type === 'LPG').length,
        electric_count: allPlots.filter(p => p.heating_type === 'Electric').length,
        compliance_due: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          return exp && (exp - now) / 86400000 <= 30;
        }).length,
        compliant: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          return !exp || (exp - now) / 86400000 > 30;
        }).length,
        expiring_7: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          const days = exp ? (exp - now) / 86400000 : 999;
          return days <= 7 && days > 0;
        }).length,
        expiring_30: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          const days = exp ? (exp - now) / 86400000 : 999;
          return days <= 30 && days > 0;
        }).length,
        expired: allCompliance.filter(a => {
          const exp = a.expiry_date ? new Date(a.expiry_date) : null;
          return exp && exp < now;
        }).length,
      });
    } catch (err) {
      console.error('Failed to fetch asset data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // URL-driven tab change
  const setActiveTab = (tab) => updateParams({ tab, search: '', park: '', status: '' });

  // URL-driven search change
  const handleSearchChange = (value) => updateParams({ search: value });

  // URL-driven park filter change
  const handleParkFilterChange = (value) => updateParams({ park: value === 'all' ? '' : value });

  // URL-driven status filter change
  const handleStatusFilterChange = (value) => updateParams({ status: value === 'all' ? '' : value });

  // Filtered data based on URL state
  const filteredParks = useMemo(() => {
    if (!urlSearch) return parks;
    const q = urlSearch.toLowerCase();
    return parks.filter(p => 
      p.name?.toLowerCase().includes(q) || 
      p.postcode?.toLowerCase().includes(q) ||
      p.address?.toLowerCase().includes(q)
    );
  }, [parks, urlSearch]);

  const filteredPlots = useMemo(() => {
    let result = [...plots];
    if (urlSearch) {
      const q = urlSearch.toLowerCase();
      result = result.filter(p => 
        p.pitch_or_unit?.toLowerCase().includes(q) || 
        p.asset_name?.toLowerCase().includes(q) ||
        p.park_name?.toLowerCase().includes(q)
      );
    }
    if (urlPark) {
      result = result.filter(p => p.park_id === urlPark || p.park_name?.toLowerCase().includes(urlPark.toLowerCase()));
    }
    return result;
  }, [plots, urlSearch, urlPark]);

  const filteredCompliance = useMemo(() => {
    let result = [...complianceAssets];
    if (urlSearch) {
      const q = urlSearch.toLowerCase();
      result = result.filter(a => 
        a.asset_name?.toLowerCase().includes(q) || 
        a.certificate_type?.toLowerCase().includes(q)
      );
    }
    if (urlStatus) {
      const now = new Date();
      switch (urlStatus) {
        case 'expired':
          result = result.filter(a => a.expiry_date && new Date(a.expiry_date) < now);
          break;
        case 'expiring':
          result = result.filter(a => {
            const exp = a.expiry_date ? new Date(a.expiry_date) : null;
            return exp && exp > now && (exp - now) / 86400000 <= 30;
          });
          break;
        case 'compliant':
          result = result.filter(a => {
            const exp = a.expiry_date ? new Date(a.expiry_date) : null;
            return !exp || (exp - now) / 86400000 > 30;
          });
          break;
        default:
          break;
      }
    }
    return result;
  }, [complianceAssets, urlSearch, urlStatus]);

  const handleAction = (action, item) => {
    switch (action) {
      case 'view-park':
        updateParams({ tab: 'plots', park: item.id || item.park_id, search: '' });
        break;
      case 'view-plot':
      case 'view-asset':
        toast.info(`View ${item.asset_name || item.pitch_or_unit}`);
        break;
      case 'new-job':
        navigate(`/phs/helpdesk?action=new-job&asset_id=${item.asset_id || item.id}`);
        break;
      case 'add-park':
      case 'edit-park':
      case 'add-asset':
      case 'edit-asset':
      case 'add-ppm':
      case 'schedule-inspection':
      case 'create-ppm-job':
        toast.info(`${action} - Modal would open`);
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wyldecrest_token');
    localStorage.removeItem('wyldecrest_user');
    navigate('/admin/login');
  };

  return (
    <PHSCommandLayout user={wyldecrestUser} onLogout={handleLogout}>
      <div className="p-4 lg:p-6" data-testid="asset-workspace">
        {/* Workspace Header */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-slate-800">Asset Workspace</h1>
          <p className="text-sm text-slate-500">Parks, plots, assets, PPM, and compliance management</p>
        </div>

        {/* KPI Strip */}
        <WorkspaceKPIStrip metrics={metrics} activeTab={activeTab} />

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto pb-px">
          {WORKSPACE_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  isActive 
                    ? 'border-teal-600 text-teal-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'parks' && (
          <ParksTabContent
            parks={filteredParks}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            searchTerm={urlSearch}
            setSearchTerm={handleSearchChange}
          />
        )}
        {activeTab === 'plots' && (
          <PlotsTabContent
            plots={filteredPlots}
            parks={parks}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            filters={{ search: urlSearch, park: urlPark || 'all' }}
            setFilters={(f) => {
              if (f.search !== undefined) handleSearchChange(f.search);
              if (f.park !== undefined) handleParkFilterChange(f.park);
            }}
          />
        )}
        {activeTab === 'register' && (
          <AssetRegisterTabContent
            assets={assets}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            filters={{ search: urlSearch }}
            setFilters={(f) => f.search !== undefined && handleSearchChange(f.search)}
          />
        )}
        {activeTab === 'ppm' && (
          <PPMTabContent
            ppmItems={ppmItems}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            filters={{ search: urlSearch }}
            setFilters={(f) => f.search !== undefined && handleSearchChange(f.search)}
          />
        )}
        {activeTab === 'compliance' && (
          <ComplianceTabContent
            assets={filteredCompliance}
            loading={loading}
            onRefresh={fetchData}
            onAction={handleAction}
            filters={{ search: urlSearch, status: urlStatus || 'all' }}
            setFilters={(f) => {
              if (f.search !== undefined) handleSearchChange(f.search);
              if (f.status !== undefined) handleStatusFilterChange(f.status);
            }}
          />
        )}
      </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(AssetWorkspace, 'helpdesk');
